import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, File, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useNotificationStore } from '../../stores/notificationStore';
import { driveService } from '../../services/driveService';
import { DriveFolder, DRIVE_FOLDERS, MAX_FILE_SIZE, SUPPORTED_FILE_TYPES, formatFileSize } from '../../types/drive';

interface FileUploadProps {
  onUploadComplete?: () => void;
  className?: string;
  targetUserId?: string; // ID dell'utente per cui caricare i file (per admin/moderator)
}

interface UploadingFile {
  file: File;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  error?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({ onUploadComplete, className = '', targetUserId }) => {
  const { user } = useAuthStore();
  const addNotification = useNotificationStore(s => s.addNotification);
  
  // Determina l'utente per cui caricare i file
  const uploadUserId = targetUserId || user?.uid;
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<DriveFolder>('documents');
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Validazione file
  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `File troppo grande. Massimo ${formatFileSize(MAX_FILE_SIZE)}`;
    }

    if (!SUPPORTED_FILE_TYPES[file.type as keyof typeof SUPPORTED_FILE_TYPES]) {
      return 'Tipo di file non supportato';
    }

    return null;
  };

  // Gestione drag & drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  }, []);

  // Gestione selezione file
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    handleFiles(files);
  }, []);

  // Processa file selezionati
  const handleFiles = useCallback(async (files: File[]) => {
    if (!uploadUserId) {
      alert('Devi essere autenticato per caricare file');
      return;
    }

    if (files.length === 0) return;

    // Validazione iniziale
    const validFiles: File[] = [];
    const invalidFiles: { file: File; error: string }[] = [];

    for (const file of files) {
      const error = validateFile(file);
      if (error) {
        invalidFiles.push({ file, error });
      } else {
        validFiles.push(file);
      }
    }

    // Mostra errori per file non validi
    if (invalidFiles.length > 0) {
      const errorMessage = invalidFiles
        .map(({ file, error }) => `${file.name}: ${error}`)
        .join('\n');
      alert(`Alcuni file non sono validi:\n${errorMessage}`);
    }

    if (validFiles.length === 0) return;

    // Inizia upload
    setIsUploading(true);
    const newUploadingFiles: UploadingFile[] = validFiles.map(file => ({
      file,
      progress: 0,
      status: 'uploading',
    }));

    setUploadingFiles(prev => [...prev, ...newUploadingFiles]);

    // Upload file per file
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      const fileIndex = uploadingFiles.length + i;

      try {
        // Verifica spazio disponibile
        const spaceCheck = await driveService.checkSpaceAvailable(uploadUserId, file.size);
        if (!spaceCheck.available) {
          setUploadingFiles(prev => prev.map((uf, idx) => 
            idx === fileIndex 
              ? { ...uf, status: 'error', error: 'Spazio insufficiente' }
              : uf
          ));
          continue;
        }

        // Carica file
        await driveService.uploadFile(uploadUserId, file, selectedFolder);

        // Aggiorna stato
        setUploadingFiles(prev => prev.map((uf, idx) => 
          idx === fileIndex 
            ? { ...uf, progress: 100, status: 'completed' }
            : uf
        ));

        // Notifica di completamento
        const isUploadingForOtherUser = targetUserId && targetUserId !== user?.uid;
        addNotification({
          type: 'info',
          title: 'File caricato con successo',
          message: isUploadingForOtherUser 
            ? `Il file "${file.name}" è stato caricato nella cartella dell'utente selezionato`
            : `Il file "${file.name}" è stato caricato con successo`
        });

        // Callback completamento
        if (onUploadComplete) {
          onUploadComplete();
        }
      } catch (error) {
        console.error('Errore upload file:', error);
        setUploadingFiles(prev => prev.map((uf, idx) => 
          idx === fileIndex 
            ? { ...uf, status: 'error', error: error instanceof Error ? error.message : 'Errore sconosciuto' }
            : uf
        ));
      }
    }

    setIsUploading(false);

    // Notifica di completamento per tutti i file
    const completedFiles = uploadingFiles.filter(uf => uf.status === 'completed');
    const failedFiles = uploadingFiles.filter(uf => uf.status === 'error');
    
    if (completedFiles.length > 0) {
      const isUploadingForOtherUser = targetUserId && targetUserId !== user?.uid;
      const message = isUploadingForOtherUser
        ? `${completedFiles.length} file caricati con successo nella cartella dell'utente selezionato`
        : `${completedFiles.length} file caricati con successo`;
      
      addNotification({
        type: 'info',
        title: 'Upload completato',
        message: message
      });
    }

    if (failedFiles.length > 0) {
      addNotification({
        type: 'warning',
        title: 'Alcuni file non sono stati caricati',
        message: `${failedFiles.length} file non sono stati caricati a causa di errori`
      });
    }

    // Pulisci input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [uploadUserId, selectedFolder, uploadingFiles.length, onUploadComplete]);

  // Rimuovi file dall'upload
  const removeUploadingFile = useCallback((index: number) => {
    setUploadingFiles(prev => prev.filter((_, idx) => idx !== index));
  }, []);

  // Pulisci file completati
  const clearCompleted = useCallback(() => {
    setUploadingFiles(prev => prev.filter(uf => uf.status === 'uploading'));
  }, []);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Area di upload */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragOver
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
          onChange={handleFileSelect}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isUploading}
        />
        
        <div className="space-y-3">
          <Upload className="mx-auto h-12 w-12 text-gray-400" />
          <div>
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              Trascina i file qui o clicca per selezionare
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              PDF, DOC, DOCX, JPG, PNG, GIF (max 5MB per file)
            </p>
          </div>
        </div>
      </div>

      {/* Indicatore upload per utente specifico */}
      {targetUserId && targetUserId !== user?.uid && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <Upload className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
              Caricamento per utente specifico
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            I file verranno caricati nella cartella dell'utente selezionato
          </p>
        </div>
      )}

      {/* Selezione cartella */}
      <div className="flex flex-wrap gap-2">
        {DRIVE_FOLDERS.map(folder => (
          <button
            key={folder.value}
            onClick={() => setSelectedFolder(folder.value)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedFolder === folder.value
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
            disabled={isUploading}
          >
            <span className="mr-2">{folder.icon}</span>
            {folder.label}
          </button>
        ))}
      </div>

      {/* Lista file in upload */}
      {uploadingFiles.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              File in caricamento
            </h3>
            <button
              onClick={clearCompleted}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Pulisci completati
            </button>
          </div>

          <div className="space-y-2">
            {uploadingFiles.map((uploadingFile, index) => (
              <div
                key={`${uploadingFile.file.name}-${index}`}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <File className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {uploadingFile.file.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatFileSize(uploadingFile.file.size)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {uploadingFile.status === 'uploading' && (
                    <div className="flex items-center space-x-2">
                      <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 transition-all duration-300"
                          style={{ width: `${uploadingFile.progress}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {uploadingFile.progress}%
                      </span>
                    </div>
                  )}

                  {uploadingFile.status === 'completed' && (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  )}

                  {uploadingFile.status === 'error' && (
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="h-5 w-5 text-red-500" />
                      <span className="text-xs text-red-600 dark:text-red-400 max-w-32 truncate">
                        {uploadingFile.error}
                      </span>
                    </div>
                  )}

                  <button
                    onClick={() => removeUploadingFile(index)}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}; 